import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import {
  UFDoraTextInput,
  DoraButton,
  UFDoraDateTimePicker,
  UFDoraDatePicker,
  UFDoraCheckbox,
} from "dora-shared/frontend";
import { useAppDispatch } from "../../../../redux-store";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import { addDriverSchedule } from "../../../../ducks/data/drivers";
import buildAddIntervalSchema, {
  AddIntervalFormData,
  endOfAllDay,
  startOfAllDay,
  toInterval,
} from "./driver-schedule-add-interval-schema";

const toApiDateTime = (date: DateTime) =>
  date.toISO({
    includeOffset: false,
    suppressSeconds: true,
    suppressMilliseconds: true,
  });

export const DriverScheduleAddInterval = (props: {
  driverId: string;
  isOpen: boolean;
  onTitleClick: () => void;
  onSubmit: () => void;
}) => {
  const { t } = useTranslation("driverSchedule");
  const { driverId, isOpen } = props;
  const [saving, setSaving] = useState(false);
  const dispatch = useAppDispatch();

  const schema = useMemo(
    () => buildAddIntervalSchema(t("endNotAfterStart")),
    [t]
  );

  const formMethods = useForm<AddIntervalFormData>({
    resolver: yupResolver(schema),
    // Show an end that is not after the start as soon as it is typed, not
    // only once Save is clicked.
    mode: "onChange",
    defaultValues: {
      startDate: startOfAllDay(DateTime.now()),
      endDate: endOfAllDay(DateTime.now()),
      allDay: true,
      absenceNote: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const startDateWatch = watch("startDate");
  const allDay = watch("allDay");

  // Moving the start past the end drags the end along to the end of the start's
  // day, rather than leaving the form invalid.
  useEffect(() => {
    if (!startDateWatch) return;
    const { endDate } = getValues();
    const interval = toInterval({ startDate: startDateWatch, endDate, allDay });
    if (interval.endDate.toMillis() <= interval.startDate.toMillis()) {
      setValue("endDate", endOfAllDay(startDateWatch), { shouldValidate: true });
    }
  }, [startDateWatch, allDay, setValue, getValues]);

  // Checking "All day" moves the times to 00:00-23:59. Unchecking it shows the
  // time pickers starting from those same times, as drivers' absences can start
  // and end at any hour.
  const allDayRegistration = register("allDay", {
    onChange: () => {
      const { startDate, endDate } = getValues();
      const interval = toInterval({ startDate, endDate, allDay: true });
      setValue("startDate", interval.startDate);
      setValue("endDate", interval.endDate, { shouldValidate: true });
    },
  });

  const onSubmit = () => {
    handleSubmit((data) => {
      setSaving(true);
      const interval = toInterval(data);
      dispatch(
        addDriverSchedule({
          driverId,
          schedule: {
            type: "INTERVAL",
            weekdays: null,
            recurrentTimeInterval: null,
            startDate: toApiDateTime(interval.startDate),
            endDate: toApiDateTime(interval.endDate),
            absenceNote: data.absenceNote,
          },
        })
      )
        .unwrap()
        .then(() => {
          reset();
          props.onSubmit();
        })
        .catch(() => {
          // The addDriverSchedule thunk has already reported it and shown the error toast.
        })
        .finally(() => {
          setSaving(false);
        });
    })();
  };

  return (
    <div>
      <div className="driver-schedule-dialog__section-title">
        <div>{t("addInterval")}</div>
        <IconButton onClick={props.onTitleClick} style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </IconButton>
      </div>
      {isOpen && (
        <div className="driver-schedule-dialog__section-content">
          <FormProvider {...formMethods}>
            <div className="flex gap-2 justify-between items-center">
              <div>
                <div className="driver-schedule-dialog__section-content-text">
                  {t("start")}
                </div>
                {allDay ? (
                  <UFDoraDatePicker
                    id="driver-schedule-interval-start"
                    control={control}
                    name={"startDate"}
                  />
                ) : (
                  <UFDoraDateTimePicker control={control} name={"startDate"} />
                )}
              </div>
              <div>
                <div className="driver-schedule-dialog__section-content-text">
                  {t("end")}
                </div>
                {allDay ? (
                  <UFDoraDatePicker
                    id="driver-schedule-interval-end"
                    minDate={startDateWatch}
                    control={control}
                    name={"endDate"}
                  />
                ) : (
                  <UFDoraDateTimePicker
                    minDateTime={startDateWatch}
                    control={control}
                    name={"endDate"}
                  />
                )}
                {errors.endDate && (
                  <div
                    style={{
                      color: "var(--error-500)",
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    {errors.endDate.message}
                  </div>
                )}
              </div>
              <div className="self-start">
                <label
                  htmlFor="driver-schedule-interval-all-day"
                  className="driver-schedule-dialog__section-content-text whitespace-nowrap cursor-pointer block"
                >
                  {t("allDay")}
                </label>
                {/* Same height as the date inputs (MUI small outlined), so the
                    box lines up with them under its label. */}
                <div className="flex items-center justify-center" style={{ height: 40 }}>
                  <UFDoraCheckbox
                    id="driver-schedule-interval-all-day"
                    register={allDayRegistration}
                  />
                </div>
              </div>
            </div>
            <div className="mt-1">
              <div className="driver-schedule-dialog__section-content-text mb-1">
                {t("absenceNote")}
              </div>
              <UFDoraTextInput
                register={register("absenceNote")}
                placeholder={t("absenceNote")}
              />
            </div>
            <div className="mt-6 mb-5">
              <DoraButton
                variant="primary-filled"
                disabled={saving}
                onClick={onSubmit}
              >
                {t("save")}
              </DoraButton>
            </div>
          </FormProvider>
        </div>
      )}
    </div>
  );
};
